import React, {useState, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {inject, observer} from "mobx-react";
// local
import {Modal} from "components/Modal";
import CustomRadioSwitch from "components/CustomRadioSwitch";
import {toRequestId} from "constants/routes.const";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import ProjectsSelect from "components/selects/ProjectsSelect";
import Select from "components/selects/Select";
import {IconFileAdd, IconCopy, IconChevronDown} from "components/icons/index";
import {hasPermission} from "helpers/helper";
import {LockValue} from "components/LockValue";
import {color} from "constants/color.consts";

export const RFPCreateModal = inject("store")(
  observer(({store: {auth, buyer}, projects, project, procurement, onClose}) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {user} = auth;
    const [loadingCopy, setLoadingCopy] = useState(false);
    const [loadingScratch, setLoadingScratch] = useState(false);
    const [isIncludeRecipients, setIncludeRecipients] = useState(false);

    const getRFPList = (project) =>
      project?.ProjectItems?.map((item) => ({
        id: item.id,
        value: item.Request.id,
        label: item.Request.name,
      })) ?? [];

    const [selectedProject, SetSelectedProject] = useState(
      projects.find((item) => item.id === project.id) ?? projects[0]
    );

    const [rfpList, setRfpList] = useState(getRFPList(selectedProject));
    const [selectedRFP, setSelectedRFP] = useState(rfpList[0]);

    const newRFPData = useMemo(
      () => ({
        name: procurement.name,
        ProjectId: project.id,
        projectItemId: procurement.id,
      }),
      [project, procurement]
    );

    const canCreateWRecipients = hasPermission(["CREATE_RFT_BY_COPY_WITH_RECIPIENTS"]);

    const createFromScratch = () => {
      setLoadingScratch(true);
      const rfpData = {
        ...newRFPData,
        ContactPersonId: procurement.UserId,
        CurrencyCode: user.ActiveOrganization?.defaultCurrencyCode,
      };
      buyer.getCurrentRequestInfo("draft-request").then(() =>
        buyer
          .updateCurrentRequestInfo(rfpData, true)
          .then((data) => navigate(toRequestId(data.rfp.id)))
          .finally(() => setLoadingScratch(false))
      );
    };

    const createFromCopy = async () => {
      setLoadingCopy(true);
      const newIdRfp = isIncludeRecipients
        ? "copy-request-with-recipients"
        : "copy-request";
      await buyer.getCurrentRequestInfo(newIdRfp, selectedRFP.value, true);
      buyer.duplicateRFPDocuments(selectedRFP.value).then((data) =>
        buyer
          .updateCurrentRequestInfo({...newRFPData, documents: data}, true)
          .then((data) => navigate(toRequestId(data.rfp.id)))
          .finally(() => setLoadingCopy(false))
      );
    };

    const onChangeProject = (id) => {
      const currentProject = projects.find((item) => item.id === id);
      SetSelectedProject(currentProject);
      setRfpList(getRFPList(currentProject));
      setSelectedRFP(getRFPList(currentProject)[0]);
    };

    const onChangeRFP = (value) =>
      setSelectedRFP(rfpList.find((item) => item.value === value));

    return (
      <Modal
        noTitleIcon
        boldTitle
        titleText="CREAT_RFP"
        rounded
        placeBitLow
        style={{marginTop: "15vh"}}
        percentageWide="45%"
        width="630"
        onClose={onClose}
      >
        <div className="rfp-create-modal__content">
          <div className="rfp-create-modal_description">
            {t("CREATE_RFP_MODAL_DESCRIPTION_01")}
          </div>
          <div className="rfp-create-modal_description mb-8">
            {t("CREATE_RFP_MODAL_DESCRIPTION_02")}
          </div>
          <div className="df">
            <div className="rfp-create-modal_create-block mr-8">
              <span className="rfp-create-modal_create-block_title">
                {t("CREATE_RFP_MODAL_TITLE_01")}
              </span>
              <div className="rfp-create-modal_create-block_data">
                <ButtonTransparent
                  name="CREATE_RFP_MODAL_BUTTON_CREATE"
                  icon={<IconFileAdd />}
                  nonTransparent
                  fullWidth
                  color="green"
                  addClass="rfp-create-modal_create-block_button"
                  onClick={createFromScratch}
                  loading={loadingScratch}
                  disabled={loadingCopy}
                />
              </div>
            </div>
            <div className="rfp-create-modal_create-block">
              <span className="rfp-create-modal_create-block_title">
                {t("CREATE_RFP_MODAL_TITLE_02")}
              </span>
              <div className="rfp-create-modal_create-block_data">
                <ProjectsSelect
                  list={projects}
                  needSearch={true}
                  onChange={onChangeProject}
                  highlightMyProjectsId={user.id}
                  noBorder
                  fullWidth
                  large
                  customIcon={<IconChevronDown color={color.purple} />}
                  defaultValue={
                    projects.find((item) => item.id === project.id)?.id ??
                    projects[0]?.id ??
                    null
                  }
                />
                <Select
                  name="rfp-select"
                  list={rfpList}
                  showArrow
                  shadow
                  large
                  fullWidth
                  suffixIcon={<IconChevronDown />}
                  value={selectedRFP?.value}
                  onChange={onChangeRFP}
                  addClass="mt-1 mb-2"
                  needSearch={true}
                  searchPlaceholder={t("SEARCH_RFP")}
                  notFoundContent="NO_RFP_FOUND"
                />

                <LockValue
                  canPermissions={["CREATE_RFT_BY_COPY_WITH_RECIPIENTS"]}
                  needLabel
                  needShowModalToSubscribe
                  addClassWrapper="mb-4"
                >
                  <CustomRadioSwitch
                    label={t("CREATE_RFP_MODAL_TOGGLE_INCLUDE")}
                    small
                    uppercase
                    checked={isIncludeRecipients}
                    isActive={(checked) =>
                      canCreateWRecipients && setIncludeRecipients(checked)
                    }
                    addWrapperClass={canCreateWRecipients ? "mb-4" : ""}
                  />
                </LockValue>

                <ButtonTransparent
                  name="CREATE_RFP_MODAL_BUTTON_COPY"
                  icon={<IconCopy color={color.white} />}
                  loading={loadingCopy}
                  disabled={loadingScratch}
                  nonTransparent
                  fullWidth
                  onClick={createFromCopy}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  })
);
